import { addHours, isToday } from 'date-fns';
import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import Checkbox from '~/components/CheckboxCustom/Checkbox';
import CloudflareResponseImage from '~/components/CloudflareResponsiveImage/CloudflareResponseImage';
import { defaultSrcSetParams } from '~/components/CloudflareResponsiveImage/constants';
import DeliveryDateLabel from '~/components/DeliveryDateLabel/DeliveryDateLabel';
import DiscountBadge from '~/components/DiscountBadge/DiscountBadge';
import ExpressDeliveryBadge from '~/components/ExpressDeliveryBadge/ExpressDeliveryBadge';
import Icon from '~/components/Icon/Icon';
import { formatVolume, pluralizePcLabel } from '~/components/ProductCard/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useDateLang } from '~/hooks/useDateLang';
import { useProductLimit } from '~/hooks/useProductLimit';
import { cartStore, mainStore, orderStore } from '~/stores';
import type { CartItem, CartItemOffer } from '~/stores/CartStore';
import { isProductBundle } from '~/types/Product';
import { formatPriceWithCurrency, toNumber } from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
} from '~/utils/multibuy';

import Counter from '../../Counter';
import Price from '../../Price';
import Tile from '../../Tile';

import { RestrictionBlock } from '../components/RestrictionBlock/RestrictionBlock';

import styles from './CartCard.module.scss';

interface CartCardProps {
  item: CartItem;
  size?: 's' | 'm' | 'l';
  location?: 'modalCart' | 'productPreview';
  className?: string;
  showCheckbox?: boolean;
}

const CartCard = ({
  item,
  size = 's',
  className,
  showCheckbox,
}: CartCardProps) => {
  const { t } = useTranslation();
  const { handleClickProduct } = useCategoryAnalytics();
  const isBundle = isProductBundle(item);
  const { locale } = useDateLang();

  const { limit, currentWhSellable, parentWhSellable, laterCount } =
    useProductLimit({
      offer: item,
    });

  const handleChangeCount = (count: number, action: 'add' | 'remove') => {
    if (item) {
      cartStore.setCartItemCountByProduct(item, count, action, 'product_main');
    }
  };

  const handleResetCount = () => {
    if (item) {
      cartStore.setCartItemCountByProduct(item, 0, 'remove', 'product_main');
    }
  };

  const applicableLevel =
    !isBundle && item.multibuyPriceLevels?.length
      ? getApplicableMultibuyLevel(item.multibuyPriceLevels, item.count)
      : null;
  const isMultiBuy = !isBundle && Boolean(item.multibuyPriceLevels?.length);
  const isMultibuyActivated = isMultiBuy && applicableLevel !== null;
  const isExpress =
    item &&
    currentWhSellable &&
    currentWhSellable >= item.count &&
    orderStore.isExpressAvailableNow;
  const isBackorderAvailable =
    item.isBackorderAvailable && item.backorderLeadTime && parentWhSellable > 0;

  const volume = isBundle
    ? `${item.bundleQuantity ?? company.config.defaultVolumeDisplay}`
    : formatVolume(item.properties, {
        defaultVolume: company.config.defaultVolumeDisplay,
      });

  const leadTime = item.backorderLeadTime ? item.backorderLeadTime : 0;

  const price =
    !isMultiBuy && item.discountPrice ? item.discountPrice : item.price;

  const renderPriceComponent = () => {
    if (isMultiBuy && applicableLevel) {
      return (
        <Price
          price={mainStore.convertPenceToPounds(
            applicableLevel.unitPrice * item.count,
          )}
          discount={mainStore.convertPenceToPounds(price * item.count)}
          classNameCurrentPrice={styles['cart-card__info-footer-price']}
          className={styles.priceContainer}
        />
      );
    }

    if (item.discountPrice) {
      return (
        <Price
          price={mainStore.convertPenceToPounds(price * item.count)}
          discount={mainStore.convertPenceToPounds(item.price * item.count)}
          classNameCurrentPrice={styles['cart-card__info-footer-price']}
          className={styles.priceContainer}
        />
      );
    }

    return (
      <Price
        price={mainStore.convertPenceToPounds(price * item.count)}
        classNameCurrentPrice={styles['cart-card__info-footer-price']}
      />
    );
  };

  let selectablePropsList = null;
  if (!isBundle && Object.keys(item.selectableProperties ?? {}).length > 0) {
    selectablePropsList = (
      <ul className={styles.propsList}>
        {Object.values((item as CartItemOffer).selectableProperties).map(
          (item, id) => (
            <li key={id} className={styles.propsListItem}>
              <span className={styles.propsListItemName}>{item.name}:</span>{' '}
              <span className={styles.propsListItemValue}>{item.value}</span>
            </li>
          ),
        )}
      </ul>
    );
  }

  return (
    <div
      id="cart-card"
      className={styles.container}
      data-company={company.name}
    >
      <div
        className={classes([styles['cart-card'], `_${size}`, className])}
        data-company={company.name}
      >
        {showCheckbox && (
          <Checkbox
            attributeName="cart-card-select"
            onChange={() =>
              item.sku && cartStore.handleSelectedCartItem(item.sku)
            }
            checked={item.selected}
          />
        )}
        <Link
          data-link="cart-card-image"
          onClick={handleClickProduct(item)}
          to={`/p/${item.slug}` + (isBundle ? '' : `?sku=${item.sku}`)}
          state={{ ...(isProductBundle(item) ? {} : { offerSku: item.sku }) }}
        >
          <Tile className={styles.cartCardTile}>
            <div className={styles.badgeList}>
              {isExpress && company.showExpressBadgeOnProductCards ? (
                <ExpressDeliveryBadge
                  className={styles.deliveryDateLabel}
                  size={'xxs'}
                />
              ) : isBackorderAvailable ? (
                <DeliveryDateLabel
                  size={'xxs'}
                  className={styles.deliveryDateLabel}
                  deliveryDate={
                    isToday(addHours(new Date(), leadTime))
                      ? null
                      : addHours(new Date(), leadTime)
                  }
                />
              ) : null}
              {item.price && toNumber(price) < toNumber(item.price) && (
                <DiscountBadge
                  currentPrice={toNumber(price)}
                  oldPrice={toNumber(item.price)}
                  size={'xxs'}
                  theme="primary"
                />
              )}
            </div>
            <CloudflareResponseImage
              src={item.previewImageThumb || ''}
              srcParams={{ width: 100, height: 140 }}
              srcSetParams={defaultSrcSetParams}
              alt=""
              className={styles.cartCardTileImage}
              loading="lazy"
            />
          </Tile>
        </Link>
        <div
          className={classes([
            styles['cart-card__info'],
            showCheckbox && styles['_row'],
          ])}
        >
          <div className={styles.cartCardInfoHeader}>
            <div className={styles.cartCardLeftBlock}>
              <Link
                data-link="cart-card-header"
                onClick={handleClickProduct(item)}
                to={`/p/${item.slug}` + (isBundle ? '' : `?sku=${item.sku}`)}
                state={{
                  ...(isProductBundle(item) ? {} : { offerSku: item.sku }),
                }}
              >
                <p className={styles.itemName}>{item.name}</p>
              </Link>
              {!selectablePropsList && (
                <p
                  className={classes([
                    styles.cartCardInfoUnit,
                    !isMultiBuy && item.discountPrice > 0 && styles.hasDiscount,
                    isMultibuyActivated && styles.hasDiscount,
                  ])}
                >
                  <span
                    className={classes([
                      styles.volume,
                      !isMultiBuy &&
                        item.discountPrice > 0 &&
                        styles.discountedValue,
                      isMultibuyActivated && styles.discountedValue,
                    ])}
                  >
                    {volume}
                  </span>{' '}
                  {formatPriceWithCurrency(
                    mainStore.convertPenceToPounds(
                      isMultiBuy && applicableLevel
                        ? applicableLevel.unitPrice
                        : price,
                    ),
                  )}
                  {isBundle && (
                    <div className={styles.specialOffer}>
                      {t('specialOffer')} -{' '}
                      <span className={styles.type}>{t('bundle')}</span>
                    </div>
                  )}
                </p>
              )}
              {selectablePropsList}
              {isMultiBuy &&
                (() => {
                  const lowestLevel = getLowestMultibuyLevel(
                    item.multibuyPriceLevels,
                  );
                  if (!lowestLevel) {
                    return null;
                  }
                  return (
                    <div className={styles.multiBuySpecialOfferWr}>
                      <span className={styles.multiBuySpecialOffer}>
                        {`${t('phrases:specialOffer')}: `}
                        <span className={styles.multiBuySpecialOffer__bold}>
                          {t('phrases:multiBuySpecialOffer', {
                            cost: formatPriceWithCurrency(
                              mainStore.convertPenceToPounds(
                                lowestLevel.unitPrice,
                              ),
                            ),
                            length: pluralizePcLabel(lowestLevel.quantity),
                          })}
                        </span>
                      </span>
                    </div>
                  );
                })()}
            </div>
            {!showCheckbox && (
              <button
                className={styles['reset-button']}
                onClick={handleResetCount}
              >
                <Icon type="close" size={24} />
              </button>
            )}
          </div>
          <div
            className={classes([
              styles['cart-card__info-footer'],
              showCheckbox && styles['_column'],
            ])}
          >
            {renderPriceComponent()}
            <div className={styles.counter}>
              <Counter
                count={item.count}
                onClick={handleChangeCount}
                size={size}
                limit={limit}
                customSize={20}
                containerClassName={styles.containerClassName}
                buttonClassName={styles.buttonClassName}
                countClassName={styles.countClassName}
              />
            </div>
          </div>
        </div>
      </div>
      {currentWhSellable > 0 && item.count > currentWhSellable ? (
        <RestrictionBlock
          date={format(addHours(new Date(), leadTime), 'd MMMM', {
            timeZone: company.config.timeZone,
            locale,
          })}
          currentCount={currentWhSellable}
          laterCount={laterCount}
        />
      ) : null}
    </div>
  );
};

export default observer(CartCard);
